import { escapeCsvField } from './csvExport';
import type { FiCsvDiagnosticRow } from './fiDiagnostics';
import { TAX_PIPELINE } from './taxPipelineConfig';
import { isTaxableIncomeLedgerEvent } from './fiIncomeReport';
import type { LedgerEvent } from './types';

/**
 * Aggregate + gap rows for income pipeline (counts, by subreason, missing EUR, ambiguous flags).
 * Merge with `buildFiDiagnosticsCsv` or export standalone.
 */
export function buildFiIncomePipelineDiagnosticsCsv(events: LedgerEvent[]): string {
  const meta = TAX_PIPELINE.fi;
  const headers = [
    'policy_mode',
    'basis_method',
    'timezone',
    'source',
    'csv_row',
    'ref',
    'transaction_kind',
    'asset',
    'category',
    'subreason',
    'note',
  ];
  const lines = [headers.map(escapeCsvField).join(',')];

  const incomeTagged = events.filter(isTaxableIncomeLedgerEvent);
  const incomeKindUntagged = events.filter(
    (e) => e.kind === 'income' && e.fiTaxCategory !== 'TAXABLE_INCOME'
  );
  const missingEurIncome = incomeTagged.filter((e) => e.eurValue == null);
  const ambiguousFlags = events.filter(
    (e) => e.reviewFlags?.includes('ambiguous_income_candidate')
  );
  const incomeValuationGap = incomeTagged.filter((e) =>
    e.reviewFlags?.includes('income_valuation_gap')
  );
  const incomeDustFmv = incomeTagged.filter((e) =>
    e.reviewFlags?.includes('income_dust_fmv')
  );

  const bySub = new Map<string, number>();
  for (const e of incomeTagged) {
    const k = e.fiSubreason ?? '(none)';
    bySub.set(k, (bySub.get(k) ?? 0) + 1);
  }

  const push = (r: FiCsvDiagnosticRow) => {
    lines.push(
      [
        meta.policyMode,
        meta.basisMethod,
        meta.timezone,
        r.source,
        String(r.csv_row),
        r.ref,
        r.transaction_kind,
        r.asset,
        r.category,
        r.subreason,
        r.note,
      ]
        .map(escapeCsvField)
        .join(',')
    );
  };

  push({
    source: 'pipeline',
    csv_row: 0,
    ref: '__income_stats__',
    transaction_kind: 'INCOME_TAGGED_COUNT',
    asset: '',
    category: 'INCOME_PIPELINE',
    subreason: 'STAT_TOTAL',
    note: String(incomeTagged.length),
  });

  push({
    source: 'pipeline',
    csv_row: 0,
    ref: '__income_stats__',
    transaction_kind: 'INCOME_KIND_UNTAGGED_COUNT',
    asset: '',
    category: 'INCOME_PIPELINE',
    subreason: 'STAT_GAP',
    note: String(incomeKindUntagged.length),
  });

  push({
    source: 'pipeline',
    csv_row: 0,
    ref: '__income_stats__',
    transaction_kind: 'INCOME_MISSING_EUR_COUNT',
    asset: '',
    category: 'INCOME_PIPELINE',
    subreason: 'STAT_VALUATION_GAP',
    note: String(missingEurIncome.length),
  });

  push({
    source: 'pipeline',
    csv_row: 0,
    ref: '__income_stats__',
    transaction_kind: 'AMBIGUOUS_INCOME_CANDIDATE_COUNT',
    asset: '',
    category: 'INCOME_PIPELINE',
    subreason: 'STAT_AMBIGUOUS',
    note: String(ambiguousFlags.length),
  });

  push({
    source: 'pipeline',
    csv_row: 0,
    ref: '__income_stats__',
    transaction_kind: 'INCOME_VALUATION_GAP_FLAG_COUNT',
    asset: '',
    category: 'INCOME_PIPELINE',
    subreason: 'STAT_VALUATION_GAP',
    note: String(incomeValuationGap.length),
  });

  push({
    source: 'pipeline',
    csv_row: 0,
    ref: '__income_stats__',
    transaction_kind: 'INCOME_DUST_FMV_FLAG_COUNT',
    asset: '',
    category: 'INCOME_PIPELINE',
    subreason: 'STAT_DUST_FMV',
    note: String(incomeDustFmv.length),
  });

  for (const [sub, n] of Array.from(bySub.entries()).sort((a, b) =>
    a[0].localeCompare(b[0])
  )) {
    push({
      source: 'pipeline',
      csv_row: 0,
      ref: '__income_by_subreason__',
      transaction_kind: 'INCOME_BY_SUBREASON',
      asset: '',
      category: 'INCOME_PIPELINE',
      subreason: sub,
      note: String(n),
    });
  }

  for (const e of missingEurIncome.slice(0, 200)) {
    push({
      source: e.source,
      csv_row: 0,
      ref: e.ref ?? '',
      transaction_kind: e.csvMeta?.transactionKind ?? 'income',
      asset: e.asset,
      category: 'INCOME_PIPELINE',
      subreason: 'MISSING_EUR_AT_RECEIPT',
      note: e.explanation ?? '',
    });
  }

  for (const e of ambiguousFlags.slice(0, 200)) {
    push({
      source: e.source,
      csv_row: 0,
      ref: e.ref ?? '',
      transaction_kind: e.csvMeta?.transactionKind ?? (e.classification ?? ''),
      asset: e.asset,
      category: 'AMBIGUOUS_REVIEW',
      subreason: 'UNMAPPED_REWARD_LIKE_TYPE',
      note: e.explanation ?? '',
    });
  }

  return lines.join('\r\n');
}
